import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { typeboxResolver } from '@hookform/resolvers/typebox'
import { Type, type Static } from '@sinclair/typebox'
import { Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth'
import { api, ApiError } from '@/lib/api'

const LoginSchema = Type.Object({
  email: Type.String({
    format: 'email',
    minLength: 5,
    errorMessage: 'Informe um email válido',
  }),
  password: Type.String({
    minLength: 1,
    errorMessage: 'Informe sua senha',
  }),
})

type LoginInput = Static<typeof LoginSchema>

interface LoginResponse {
  token: string
  user: {
    id: string
    email: string
    name: string
    role: string
    active: boolean
    lastLoginAt: string | null
  }
}

/**
 * Real login flow (Story 1.7 §AC #11):
 *   - submits credentials to `POST /admin/auth/login`
 *   - on 200: writes user + token into the Zustand store, redirects /dashboard
 *   - on 401: shows "Email ou senha inválidos" inline (anti-enumeration —
 *     never reveals whether email or password was wrong)
 *   - on network failure: shows "Erro de conexão, tente novamente"
 */
export function LoginPage(): React.ReactElement {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: typeboxResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null)
    setSubmitting(true)
    try {
      const result = await api<LoginResponse>('/admin/auth/login', {
        method: 'POST',
        body: JSON.stringify(values),
      })
      setAuth(
        {
          id: result.user.id,
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
        },
        result.token,
      )
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setServerError('Email ou senha inválidos')
        } else if (err.status === 429) {
          setServerError('Muitas tentativas. Tente novamente em alguns instantes.')
        } else {
          setServerError('Erro ao entrar. Tente novamente.')
        }
      } else {
        // TypeError / network error / DNS failure / CORS
        setServerError('Erro de conexão, tente novamente')
      }
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" aria-hidden="true" />
        </div>
        <CardTitle>SDR Agend Admin</CardTitle>
        <CardDescription>Entre com suas credenciais para continuar</CardDescription>
      </CardHeader>
      <CardContent>
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email')}
            />
            {errors.email ? (
              <p id="email-error" role="alert" className="text-xs text-destructive">
                {errors.email.message ?? 'Email inválido'}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.password ? 'true' : 'false'}
              aria-describedby={errors.password ? 'password-error' : undefined}
              {...register('password')}
            />
            {errors.password ? (
              <p id="password-error" role="alert" className="text-xs text-destructive">
                {errors.password.message ?? 'Senha inválida'}
              </p>
            ) : null}
          </div>

          {serverError ? (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default LoginPage
